import {
  PLATFORM_CATALOG,
  type PlatformCatalogEntry,
  platformPopularityRank,
} from "@/lib/platforms";

/**
 * How many networks the announcement bar names outright before the rest become
 * a count. Keeps the sentence to one line as more adapters ship — the bar links
 * to `/integrations` for the full list anyway.
 */
const NAMED_NETWORK_LIMIT = 3;

/** `["A", "B", "C"] → "A, B and C"`. No Oxford comma, matching the hero line. */
function joinWithAnd(labels: readonly string[]): string {
  if (labels.length <= 1) {
    return labels[0] ?? "";
  }

  return `${labels.slice(0, -1).join(", ")} and ${labels.at(-1)}`;
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

/**
 * The landing announcement bar's copy, derived from the catalog.
 *
 * Exported as a function over an injected catalog so the counting can be tested
 * against known inputs without mocking the module (see `no-module-mocking`).
 * `buildAnnouncementCopy(PLATFORM_CATALOG)` is what actually ships.
 *
 * `PLATFORM_CATALOG` deliberately excludes the `mock` dev channel — it lives in
 * the separate `MOCK_PLATFORM` export — so nothing here has to filter it out.
 * A test pins that, because the day it moves into the catalog this copy would
 * otherwise advertise a test channel on the landing page.
 */
export function buildAnnouncementCopy(
  catalog: readonly PlatformCatalogEntry[],
): string {
  const live = catalog
    .filter((entry) => entry.status === "live")
    .sort(
      (a, b) => platformPopularityRank(a.id) - platformPopularityRank(b.id),
    );
  const soonCount = catalog.length - live.length;
  const suffix = soonCount > 0 ? ` — with ${soonCount} more coming soon.` : ".";

  if (live.length === 0) {
    return `PostDaily plans, queues and publishes your posts${suffix}`;
  }

  const named = live.slice(0, NAMED_NETWORK_LIMIT).map((entry) => entry.label);
  const unnamed = live.length - named.length;
  // The count is the final list item, so it joins as "A, B, C and 3 other
  // networks" rather than "A, B and C and 3 other networks".
  const networks = joinWithAnd(
    unnamed > 0 ? [...named, plural(unnamed, "other network")] : named,
  );

  return `PostDaily schedules to ${networks}${suffix}`;
}

/**
 * Derived from the catalog, never hand-written.
 *
 * This bar used to read "PostDaily schedules to Bluesky, Mastodon & Facebook —
 * with more networks coming soon." while the catalog already marked six
 * networks live: it was missing X, Instagram and Threads, so the page most
 * traffic lands on undersold the product by half, and named the three networks
 * fewest people use.
 *
 * It was the same hardcoding that once let the onboarding wizard advertise live
 * networks as "coming soon" (see `onboarding/connect-providers.test.ts`) and
 * that pointed the dashboard checklist at Bluesky alone (see
 * `dashboard/onboarding-card.tsx`). Third time — so this one is derived and
 * pinned rather than kept in sync by comment.
 */
export const ANNOUNCEMENT_COPY = buildAnnouncementCopy(PLATFORM_CATALOG);
